import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { fetchTicketComment, ticketChangesQuery, type TicketChangeEvent, type TicketChangeRow } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { agoOrEmpty } from './format'
import './TicketChanges.css'

/**
 * Fila "History" - `easeedesk/tickets/details/changes.php` cu constructorul
 * `TicketChanges` din `public/js/easeedesk/easeedesk.js`.
 *
 * Aceeasi linie a timpului: o pastila colorata cu pictograma actiunii, cine a
 * facut-o si cand, si sub ea randurile schimbate - de la ce, la ce.
 *
 * TEXTELE LUNGI RAMAN INCHISE pana se cer. "read the comment" desface textul
 * comentariului chiar sub rand si il strange la al doilea clic - ca
 * `.comment-details` cu `collapse('toggle')` din portal. Nu mai e o legatura:
 * ducea la `/easeedesk/getTicketCommentDetails/{c}`, o ruta a portalului vechi
 * care pe optimumdesk.ai nu exista. Textul vine din `GET /easeedesk/comments/{c}`
 * (aceeasi poarta ca fila) si se deseneaza intr-un cadru FARA scripturi
 * (`sandbox`), ca descrierea din Summary: HTML-ul scris de om nu ajunge in pagina.
 */

const FILTERS_HELP: Record<string, string> = {
  all: 'Everything that happened on this ticket.',
  updates: 'Only the changes, without the comments.',
  comments: 'Only the comments.',
}

export function TicketChanges({ id }: { id: number }) {
  const [filter, setFilter] = useState('all')
  const { data, error } = useQuery(ticketChangesQuery(id, filter))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div id={`chTab${id}`}>
      <div className="btn-group ch-filters" role="group">
        {data.filters.map((f) => (
          <button
            key={f.key}
            type="button"
            title={FILTERS_HELP[f.key]}
            className={`btn btn-sm btn-theme-inverse btn-transparent${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {data.events.length === 0 ? (
        <div className="note note-info ch-empty">
          <i className="fas fa-info-circle" aria-hidden="true"></i>{' '}
          {filter === 'comments' ? 'No comments for this ticket' : 'Nothing has happened on this ticket yet'}
        </div>
      ) : (
        <TicketChangesTimeline ticket={id} events={data.events} readComment={fetchTicketComment} />
      )}
    </div>
  )
}

/**
 * Linia timpului, singura, fara casutele de filtrare si fara cererea ei.
 *
 * Exportata fiindca o deseneaza si pagina pe care o deschide CLIENTUL din
 * email (`CustomerTicketPage`), unde intrarile vin gata citite in raspunsul
 * paginii, nu dintr-un capat al ei.
 *
 * `links` inchide legatura catre emailul intreg: duce in portalul vechi si
 * cere sesiune, iar clientul care a deschis linkul din email nu are una. Fara
 * `readComment`, butonul comentariului nu apare deloc - nu exista legatura de
 * rezerva spre pagina veche.
 */
export function TicketChangesTimeline({ ticket, events, links = true, readComment }: {
  ticket: number
  events: TicketChangeEvent[]
  links?: boolean
  /**
   * Pagina clientului: comentariul se citeste prin jetonul ei
   * (`getTicketCommentDetailsCustomer`) si se desface sub rand, intr-un
   * iframe, ca in `changes_customer.php`.
   */
  readComment?: (comment: number) => Promise<{ comment: string }>
}) {
  return (
    <div className="historytimeline ch-timeline">
      {events.map((event) => (
        <Event key={event.id} ticket={ticket} event={event} links={links} readComment={readComment} />
      ))}
    </div>
  )
}

/**
 * `.comment-details` desfacut sub rand: textul comentariului, in iframe, ca in
 * portal. Cadrul n-are voie la scripturi (`sandbox` fara `allow-scripts`);
 * `allow-same-origin` ramane doar ca sa i se poata masura inaltimea, ca la
 * `resizeIframe` din portal si la descrierea din Summary.
 */
function InlineComment({ comment, readComment }: { comment: number; readComment: (comment: number) => Promise<{ comment: string }> }) {
  const [open, setOpen] = useState(false)
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [height, setHeight] = useState(0)
  const frame = useRef<HTMLIFrameElement>(null)

  const fit = (): void => {
    const doc = frame.current?.contentDocument
    if (doc?.body) setHeight(doc.body.scrollHeight)
  }
  useEffect(fit)

  return (
    <>
      <button
        type="button"
        className="btn btn-xs btn-primary"
        data-value={comment}
        data-url="comment"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was)
          if (html === null) {
            readComment(comment)
              .then((out) => setHtml(out.comment))
              .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Comment not found.'))
          }
        }}
      >
        read the comment
      </button>
      {open ? (
        <div className="comment-details">
          {error !== '' ? (
            <span className="text-danger">{error}</span>
          ) : html === null ? (
            <Spinner />
          ) : (
            <iframe
              ref={frame}
              className="description-content"
              title={`comment-${comment}`}
              sandbox="allow-same-origin"
              srcDoc={`<!doctype html><meta charset="utf-8"><style>body{margin:0 5px;word-wrap:normal;overflow-wrap:anywhere}img{max-width:100%}</style>${html}`}
              width="100%"
              height={height}
              style={{ border: 0, display: 'block' }}
              onLoad={fit}
            ></iframe>
          )}
        </div>
      ) : null}
    </>
  )
}

function Event({ ticket, event, links, readComment }: {
  ticket: number
  event: TicketChangeEvent
  links: boolean
  readComment?: (comment: number) => Promise<{ comment: string }>
}) {
  const when = new Date(event.at.replace(' ', 'T'))
  const valid = !Number.isNaN(when.getTime())

  return (
    <div className="media ch-event">
      <div className="media-left">
        <i className={`${event.icon} media-object ch-icon`} style={{ backgroundColor: event.color || '#98a1aa' }}></i>
      </div>
      <div className="media-body">
        <p className="ch-title">
          {/* "The system automatically" era textul cand autorul lipsea; il pastram. */}
          <span className="color-green-meadow">{event.author || 'The system automatically'}</span> {event.action}
          <EscalationTarget escalation={event.escalation} />
          {valid && (
            <>
              {' on '}
              <span className="color-green-meadow">{when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
              {' at '}
              <span className="color-green-meadow">
                {String(when.getHours()).padStart(2, '0')}:{String(when.getMinutes()).padStart(2, '0')}
              </span>{' '}
              <em>({agoOrEmpty(event.at)})</em>
            </>
          )}
        </p>

        {event.changes.map((change, i) => (
          <Change key={`${event.id}-${i}`} ticket={ticket} change={change} links={links} readComment={readComment} />
        ))}
      </div>
    </div>
  )
}

/**
 * " from Helpdesk L1 / Ion Pop to Ana Pop" dupa "escalated ticket". Fara el,
 * randul spunea doar ca tichetul a fost escaladat, nu si cui. Felul nu se
 * mai pune aici: are randul lui dedesubt, "Escalation type", ca in portal.
 */
function EscalationTarget({ escalation }: { escalation?: TicketChangeEvent['escalation'] }) {
  if (!escalation) return null
  return (
    <>
      {escalation.from ? (
        <>
          {' from '}
          <span className="color-green-meadow">{escalation.from}</span>
        </>
      ) : null}
      {escalation.to ? (
        <>
          {' to '}
          <span className="color-green-meadow">{escalation.to}</span>
        </>
      ) : null}
    </>
  )
}

function Change({ ticket, change, links, readComment }: {
  ticket: number
  change: TicketChangeRow
  links: boolean
  readComment?: (comment: number) => Promise<{ comment: string }>
}) {
  /* Un comentariu fara cale de citire ar ramane un "Comment:" gol. */
  if (change.kind === 'comment' && (!readComment || change.ref === null || !/^\d+$/.test(change.ref))) return null

  return (
    <p className="ch-change">
      <span className="ch-field">{change.title}:</span>{' '}
      {change.kind === 'comment' ? (
        readComment && change.ref !== null ? (
          <InlineComment comment={Number(change.ref)} readComment={readComment} />
        ) : null
      ) : (
        <>
          {change.old.map((value, i) => (
            <span className="ch-old" key={`o${i}`}>
              {value}
            </span>
          ))}
          {change.old.length > 0 && change.new.length > 0 && <i className="fa fa-angle-right ch-arrow" aria-hidden="true"></i>}
          {change.new.map((value, i) => (
            <span className={change.kind === 'email' ? 'ch-plain' : 'ch-new'} key={`n${i}`}>
              {value}
            </span>
          ))}
          {links && change.kind === 'email' && change.ref !== null && (
            <a
              className="btn btn-xs btn-primary ch-more"
              href={`/conversation/ticketChangesEmailBody/${change.ref}/${ticket}`}
              target="_blank"
              rel="noreferrer"
            >
              read the e-mail
            </a>
          )}
        </>
      )}
    </p>
  )
}
